import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ChartContainer as StyledChartContainer,
  ChartTitle,
  ChartSubtitle,
  ChartSource,
} from '@fuww/library/src/Charts/Styled';

interface ChartBaseProperties {
  title?: string;
  subtitle?: string;
  source?: string;
  data: Record<string, unknown>[];
  height?: number;
  width?: string;
  dataKey: string;
  valuePrefix?: string;
  valueSuffix?: string;
  valueLabel?: string;
}

interface BarChartProperties extends ChartBaseProperties {
  xAxisKey: string;
  color?: string;
  valueScale?: number;
}

interface LineChartProperties extends ChartBaseProperties {
  xAxisKey: string;
  color?: string;
  yDomain?: [number, number];
}

interface PieChartProperties extends ChartBaseProperties {
  nameKey: string;
  colors?: string[];
}

interface ChartContainerProperties {
  title?: string;
  subtitle?: string;
  source?: string;
  children: React.ReactNode;
}

const ChartContainer = ({
  title,
  subtitle,
  source,
  children,
}: ChartContainerProperties) => (
  <StyledChartContainer>
    {title && <ChartTitle>{title}</ChartTitle>}
    {subtitle && <ChartSubtitle>{subtitle}</ChartSubtitle>}
    {children}
    {source && (
      <ChartSource>
        Source:
        {' '}
        {source}
      </ChartSource>
    )}
  </StyledChartContainer>
);

export const BarChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  xAxisKey,
  valuePrefix = '',
  valueSuffix = '',
  valueLabel = 'Value',
  color = '#8884d8',
  valueScale = 1,
  height = 400,
  width = '100%',
}: BarChartProperties) => (
  <ChartContainer title={title} subtitle={subtitle} source={source}>
    <ResponsiveContainer width={width} height={height}>
      <RechartsBarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisKey} />
        <YAxis
          tickFormatter={(value) => (
            `${valuePrefix}${valueScale === 1
              ? value : (value / valueScale).toFixed(1)}${valueSuffix}`
          )}
        />
        <Tooltip />
        <Legend />
        <Bar
          dataKey={dataKey}
          name={valueLabel}
          fill={color}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  </ChartContainer>
);

export const LineChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  xAxisKey,
  valuePrefix = '',
  valueSuffix = '',
  valueLabel = 'Value',
  color = '#82ca9d',
  yDomain,
  height = 400,
  width = '100%',
}: LineChartProperties) => (
  <ChartContainer title={title} subtitle={subtitle} source={source}>
    <ResponsiveContainer width={width} height={height}>
      <RechartsLineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisKey} />
        <YAxis
          domain={yDomain}
          tickFormatter={(value) => `${valuePrefix}${value}${valueSuffix}`}
        />
        <Tooltip
          formatter={(value) => [
            `${valuePrefix}${value}${valueSuffix}`,
            valueLabel,
          ]}
        />
        <Legend />
        <Line
          type="monotone"
          name={valueLabel}
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          dot={{ r: 6 }}
          activeDot={{ r: 8 }}
        />
      </RechartsLineChart>
    </ResponsiveContainer>
  </ChartContainer>
);

export const PieChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  nameKey,
  valuePrefix = '',
  valueSuffix = '',
  valueLabel = 'Value',
  colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'],
  height = 400,
  width = '100%',
}: PieChartProperties) => (
  <ChartContainer title={title} subtitle={subtitle} source={source}>
    <ResponsiveContainer width={width} height={height}>
      <RechartsPieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine
          outerRadius={150}
          fill="#8884d8"
          dataKey={dataKey}
          nameKey={nameKey}
          label={({ name, value }) => (
            `${name}: ${valuePrefix}${value}${valueSuffix}`
          )}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${entry[nameKey]}`}
              fill={colors[index % colors.length]}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => [
            `${valuePrefix}${value}${valueSuffix}`,
            valueLabel,
          ]}
        />
        <Legend />
      </RechartsPieChart>
    </ResponsiveContainer>
  </ChartContainer>
);

export { ChartGrid } from '@fuww/library/src/Charts/Styled';
